import React from 'react';
import { useQuery } from 'react-apollo';
import { t, defineMessages } from '@sm/intl';
import { createURL } from '@sm/utils';
import { TopBarFull } from '../../TopBarFull';
import {
  TopBarFullCtaColors,
  TopBarFullUseCaseEnums,
} from '../../TopBarFullEnums';
import funImage from '../../assets/UCS_Just_For_Fun.png';
import useCaseJustForFunTopBarFullQuery from './UseCaseJustForFunTopBarFull.graphql';
import TopBarFullSkeleton from '../../TopBarFullSkeleton';
import { EmptyComponent } from '../../../helpers/EmptyComponent';

const DEFAULT_UTSOURCE2 = 'full_misc_upsell_just_for_fun';
const DEFAULT_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseJustForFunTopBarFull.headline',
    defaultMessage: 'Improve the quiz experience with Quizzes Pro',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseJustForFunTopBarFull.message',
    defaultMessage:
      'Make quizzes more engaging with feedback based on answer accuracy.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseJustForFunTopBarFull.ctaText',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

const INITIATED_DRAFTED_UTSOURCE2 = 'full_initiate_upsell_just_for_fun';
const INITIATED_DRAFTED_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseJustForFunTopBarFull.headlineInitiated',
    defaultMessage: 'Improve the quiz experience with Quizzes Pro',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseJustForFunTopBarFull.messageInitiated',
    defaultMessage:
      'Make quizzes more engaging with feedback based on answer accuracy.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseJustForFunTopBarFull.ctaTextInitiated',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

const TESTED_ACTIVATED_UTSOURCE2 = 'full_activate_upsell_just_for_fun';
const TESTED_ACTIVATED_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseJustForFunTopBarFull.headlineTested',
    defaultMessage: 'Give everyone a say with unlimited responses',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseJustForFunTopBarFull.messageTested',
    defaultMessage:
      'Your plan shows the first { analyzeResponseLimit } responses. Unlock the rest with an upgraded plan.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseJustForFunTopBarFull.ctaTextTested',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

const DEPLOYED_UTSOURCE2 = 'full_deploy_upsell_just_for_fun';
const DEPLOYED_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseJustForFunTopBarFull.headlineDeployed',
    defaultMessage: 'Make sense of answers to open-ended questions',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseJustForFunTopBarFull.messageDeployed',
    defaultMessage:
      'Use advanced text analysis and word clouds to find the trends in survey comments.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseJustForFunTopBarFull.ctaTextDeployed',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

export type UseCaseJustForFunTopBarFullProps = {
  showLoaderOnFetch?: boolean;
  variation?: TopBarFullUseCaseEnums | null;
};

const VariationToCopy = {
  [TopBarFullUseCaseEnums.InitiatedDrafted]: {
    copy: INITIATED_DRAFTED_COPY,
    utSource2: INITIATED_DRAFTED_UTSOURCE2,
  },
  [TopBarFullUseCaseEnums.TestedActivated]: {
    copy: TESTED_ACTIVATED_COPY,
    utSource2: TESTED_ACTIVATED_UTSOURCE2,
  },
  [TopBarFullUseCaseEnums.Deployed]: {
    copy: DEPLOYED_COPY,
    utSource2: DEPLOYED_UTSOURCE2,
  },
};

export const UseCaseJustForFunTopBarFull: React.FC<UseCaseJustForFunTopBarFullProps> = ({
  showLoaderOnFetch,
  variation,
}) => {
  const LoaderComponent: React.FC = showLoaderOnFetch
    ? TopBarFullSkeleton
    : EmptyComponent;

  // Fetch data required for component
  const { loading, error, data } = useQuery(useCaseJustForFunTopBarFullQuery);
  const analyzeResponseLimit = data?.user?.limits?.analyzeResponseLimit;

  const [copy, utSource2] =
    variation && variation in VariationToCopy
      ? [VariationToCopy[variation].copy, VariationToCopy[variation].utSource2]
      : [DEFAULT_COPY, DEFAULT_UTSOURCE2];
  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : error || !analyzeResponseLimit ? (
        <EmptyComponent />
      ) : (
        <TopBarFull
          headline={t(copy.HEADLINE)}
          message={t(copy.MESSAGE, { analyzeResponseLimit }, { html: true })}
          image={() => <img src={funImage} aria-hidden="true" />}
          ctaText={t(copy.CTA_TEXT)}
          ctaLink={createURL('/pricing/upgrade/', {
            ut_source: 'topbar',
            ut_source2: utSource2,
          })}
          ctaNewTab={true}
          ctaColor={TopBarFullCtaColors.alt}
        />
      )}
    </>
  );
};

export default UseCaseJustForFunTopBarFull;
